import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Download, FileSignature, Loader2, PenLine, Undo2 } from "lucide-react";
import { SowCountersign } from "./SowCountersign";
import { ReasonForm, StaffAmendPanel, useCanAct } from "./StaffAmendPanel";

const API = process.env.REACT_APP_BACKEND_URL;

function ReturnForChanges({ data, onChanged }) {
  const canAct = useCanAct(data.bd_email);
  const [open, setOpen] = useState(false);
  if (!canAct) return null;
  const submit = async (reason) => {
    try {
      await axios.post(`${API}/api/sow/${data.cid}/return`, { reason });
      toast.success("Returned to the client. Their signature has been set aside and they have been emailed.");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? (d.missing || []).join(", ") : d) || "Could not return the document.");
    }
  };
  if (open) {
    return (
      <ReasonForm
        label="What needs to change before you can countersign"
        hint="Sent to the client word for word. Their signature is set aside; they correct the details and sign again. If the proposal itself must change, update Cognito first."
        placeholder="e.g. The purchase order number entered belongs to last year's project; please enter the PO raised for this study."
        cta="Return to the client for changes" tone="amber" minLength={5} onSubmit={submit} onCancel={() => setOpen(false)} testid="staff-return-form" />
    );
  }
  return (
    <button type="button" onClick={() => setOpen(true)} className="self-start inline-flex items-center gap-2 rounded-full border border-amber-400/60 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-400/10 transition-colors" data-testid="staff-return-open">
      <Undo2 className="w-4 h-4" /> Return to the client for changes instead
    </button>
  );
}

// Medicys only panel at the top of the proposal viewer: countersign a
// client signed SOW, return it for changes, or amend and re-issue an
// executed one. Hidden until the client has signed something.
export function StaffCountersignPanel({ token, onExecuted = null, initial = null }) {
  const [data, setData] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const url = `${API}/api/public/proposals/${encodeURIComponent(token)}/commission/countersign-review`;

  const load = useCallback(async () => {
    if (initial) return initial;
    try {
      const { data: d } = await axios.get(url, { withCredentials: true });
      setData(d);
      return d;
    } catch (_e) {
      setData(null);
      return null;
    }
  }, [url, initial]);

  useEffect(() => { load(); }, [load]);

  // After countersigning, the executed copy is rendered in the background; poll until it lands.
  useEffect(() => {
    if (!submitted || !data || data.signed || data.client_flow?.stage === "countersign_failed") return undefined;
    const t = setTimeout(async () => {
      const d = await load();
      if (d?.signed) onExecuted?.();
    }, 5000);
    return () => clearTimeout(t);
  }, [submitted, data, load, onExecuted]);

  if (!data || !data.client_flow) return null;
  const flow = data.client_flow;
  const changed = () => { load(); onExecuted?.(); };
  const show = data.awaiting || data.countersigning || data.signed || data.amendment_pending;
  if (!show) return null;
  const headline = data.amendment_pending
    ? `An amended Statement of Work (version ${flow.amendment?.version}) is with the client for signature.`
    : data.signed
      ? `This Statement of Work is fully executed${flow.version > 1 ? ` (version ${flow.version})` : ""}.`
      : `${data.client_company || "The client"} signed this Statement of Work in the viewer.`;

  return (
    <section className="no-print mx-4 sm:mx-6 lg:mx-10 mt-4 rounded-xl border border-[#1B4F8A]/30 bg-[#0B1220] text-stone-100 p-5 sm:p-6 flex flex-col gap-4" data-testid="staff-countersign-panel" data-stage={flow.stage}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[#3EA8DC]/15 text-[#9FD8F2] flex items-center justify-center shrink-0">{data.signed || data.amendment_pending ? <FileSignature className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}</span>
        <div className="flex flex-col gap-1.5 text-[15px] leading-relaxed">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#9FD8F2]">Medicys only · Statement of Work</p>
          <p>
            <span className="font-semibold">{headline}</span>
            {data.awaiting && " Check the signed copy, then countersign below to execute it and send the client the final document."}
          </p>
        </div>
      </div>

      {data.countersigning && (
        <div className="flex items-center gap-2.5 rounded-lg bg-white/5 px-4 py-3 text-sm" data-testid="staff-countersign-progress">
          <Loader2 className="w-4 h-4 animate-spin text-[#9FD8F2]" />
          Rendering the fully executed document, filing it in Cognito and emailing the client. This takes about a minute.
        </div>
      )}

      {submitted && data.signed && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-4 py-3 text-sm" data-testid="staff-countersign-done">
          <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          <span>Fully executed. The client has been emailed the signed Statement of Work.</span>
        </div>
      )}

      {data.signed && data.executed_doc_id && (
        <a href={`${API}/api/sow/${data.cid}/documents/${data.executed_doc_id}/download`} className="self-start inline-flex items-center gap-1.5 text-sm text-[#9FD8F2] underline underline-offset-2" data-testid="staff-countersign-download">
          <Download className="w-3.5 h-3.5" /> Download the executed PDF{flow.version > 1 ? ` (version ${flow.version})` : ""}
        </a>
      )}

      {data.awaiting && (
        <>
          <div className="rounded-lg bg-white text-stone-900 p-1 max-w-3xl">
            <SowCountersign
              deal={{ cid: data.cid, client_flow: flow, cognito: { bd_email: data.bd_email }, bd_poc: data.bd_poc }}
              onDone={() => { setSubmitted(true); load(); }}
            />
          </div>
          <ReturnForChanges data={data} onChanged={changed} />
        </>
      )}

      {(data.signed || data.amendment_pending) && <StaffAmendPanel data={data} onChanged={changed} />}
    </section>
  );
}
